import { ReactNode } from 'react';

import { TransportKind } from '@/graphql';
import unreachable from '@/utils/unreachable';
import {
  DropAnimation,
  PointerActivationConstraint,
  defaultDropAnimation,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';

import {
  STEP_SECONDS_FOR_TIME_RANGE,
  getShortTimeDivisorUnitForTimeRange,
  getTimeDivisorUnitForTimeRange,
} from './constants';
import {
  AnyChart,
  Chart,
  ChartConfig,
  GetAvailableMetricsConfigArgs,
  LabelFormatter,
  RelativeTimeRange,
  Section,
  SharedEndpointChart,
  TimeRange,
  TrainingJobChart,
  ValueFormatter,
} from './types';

export function getSecondsInTimeRange(range: RelativeTimeRange): number {
  const ONE_HOUR = 60 * 60;

  switch (range) {
    case RelativeTimeRange.LAST_10_MINUTES:
      return 10 * 60;
    case RelativeTimeRange.LAST_1_HOUR:
      return ONE_HOUR;
    case RelativeTimeRange.LAST_6_HOURS:
      return 6 * ONE_HOUR;
    case RelativeTimeRange.LAST_24_HOURS:
      return 24 * ONE_HOUR;
    case RelativeTimeRange.LAST_3_DAYS:
      return 3 * 24 * ONE_HOUR;
    case RelativeTimeRange.LAST_7_DAYS:
      return 7 * 24 * ONE_HOUR;
    default:
      unreachable(range);
  }
}

export const mapChartToTitle: Record<AnyChart, string> = {
  [Chart.InferenceVolume]: 'Inference volume',
  [Chart.InferenceTime]: 'Inference time',
  [Chart.EndToEndLatency]: 'End-to-end response time',
  [Chart.RequestSize]: 'Request size',
  [Chart.ResponseSize]: 'Response size',
  [Chart.TimeToFirstByte]: 'Time to first byte',
  [Chart.ReplicaCount]: 'Replica count',
  [Chart.CpuUsage]: 'CPU usage',
  [Chart.CpuMemory]: 'CPU memory',
  [Chart.GpuUsage]: 'GPU usage',
  [Chart.GpuMemory]: 'GPU memory',
  [Chart.TimeInAsyncQueue]: 'Time in async queue',
  [Chart.AsyncQueueSize]: 'Async queue size',
  [Chart.OngoingWebsocketConnections]: 'Ongoing websocket connections',
  [Chart.SpeculationRate]: 'Acceptance rate',
  [Chart.KVCacheHitRate]: 'KV cache hit rate',
  [SharedEndpointChart.TokensUsage]: 'Tokens usage',
  [SharedEndpointChart.RateLimitVolume]: 'Rate limit volume',
  [TrainingJobChart.GpuMemoryUsageBytes]: 'GPU memory',
  [TrainingJobChart.GpuUtilization]: 'GPU usage',
  [TrainingJobChart.MemoryUsageBytes]: 'CPU memory',
  [TrainingJobChart.EphemeralStorageUsageBytes]: 'Ephemeral storage',
  [TrainingJobChart.ReadWriteCacheUsageBytes]: 'Read-write cache',
};

export function getAbsoluteTimeRangeFromRelative(range: RelativeTimeRange): TimeRange {
  if (range === RelativeTimeRange.LAST_7_DAYS) {
    const end = new Date();
    const start = new Date(end);
    start.setDate(end.getDate() - 6);
    return [start, end];
  }

  // Round down to the nearest multiple of the step size
  const divisor = STEP_SECONDS_FOR_TIME_RANGE[range] * 1000;
  const end = new Date(Math.floor(Date.now() / divisor) * divisor);
  const start = new Date(end);
  start.setSeconds(start.getSeconds() - getSecondsInTimeRange(range));
  return [start, end];
}

export function getPollingInterval(relativeTimeRange: RelativeTimeRange) {
  // No more than once a minute
  return Math.min(STEP_SECONDS_FOR_TIME_RANGE[relativeTimeRange], 60) * 1000;
}

export function formatNumber(value: number, maximumFractionDigits = 3): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits,
  }).format(value);
}

export function getLabelRenderer(timeRange: TimeRange) {
  return function renderLabel(labelDef: string | LabelFormatter): string {
    if (typeof labelDef === 'string') {
      return labelDef;
    }

    return labelDef(getTimeDivisorUnitForTimeRange(timeRange));
  };
}

export function getValueRenderer(timeRange: TimeRange) {
  return function renderValue(
    value: number,
    valueFormatter: ValueFormatter = (value) => formatNumber(value),
  ): ReactNode {
    return valueFormatter(value, getShortTimeDivisorUnitForTimeRange(timeRange));
  };
}

export const mapSectionToTitle: Record<Section, string> = {
  [Section.Overview]: 'Overview',
  [Section.Async]: 'Async',
};

export const defaultDropAnimationConfig: DropAnimation = {
  ...defaultDropAnimation,
  // See https://basetenlabs.slack.com/archives/C01HEHX9GSW/p1666364303731739
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: { opacity: '0.5' },
    },
  }),
};

export function noopStrategy(): null {
  return null;
}

export function getAvailableMetricsConfig({
  config,
  showGpuCharts,
  transportKind,
  shouldFilterOutNonHttpTransportMetrics,
  speculativeDecodingMetricsEnabled,
}: GetAvailableMetricsConfigArgs): ChartConfig[] {
  let filteredConfig = config;

  if (transportKind !== TransportKind.Http && shouldFilterOutNonHttpTransportMetrics) {
    filteredConfig = config.filter(
      (c) =>
        ![Chart.AsyncQueueSize, Chart.TimeInAsyncQueue, Chart.InferenceTime].includes(
          c.id as Chart,
        ),
    );
  }

  if (transportKind !== TransportKind.Websocket) {
    filteredConfig = filteredConfig.filter((c) => c.id !== Chart.OngoingWebsocketConnections);
  }

  if (!speculativeDecodingMetricsEnabled) {
    filteredConfig = filteredConfig.filter(
      (c) => ![Chart.SpeculationRate, Chart.KVCacheHitRate].includes(c.id as Chart),
    );
  }

  if (!showGpuCharts) {
    return filteredConfig.filter((c) => ![Chart.GpuUsage, Chart.GpuMemory].includes(c.id as Chart));
  }

  if (!filteredConfig.some((c) => c.id === Chart.GpuUsage || c.id === Chart.GpuMemory)) {
    return [
      ...filteredConfig,
      { id: Chart.GpuUsage, hidden: false },
      { id: Chart.GpuMemory, hidden: false },
    ];
  }

  return filteredConfig;
}

export function validateMetricsConfig(config: ChartConfig[]) {
  // we had a typo in RequestSize chart key
  // it was `reques-size` instead of `request-size`

  // we fixed the typo
  // this validation step is to ensure that we don't break the existing users
  return config.map((c) => ({
    ...c,
    id: (c.id as string) === 'reques-size' ? Chart.RequestSize : c.id,
  }));
}

export const activationConstraint: PointerActivationConstraint = { distance: 10 };
